import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Plus } from 'lucide-react'
import { Button } from 'components/ui/Button'
import { Input } from 'components/ui/Input'
import { cn } from 'lib/utils'

interface SingleFieldWithLabelsProps {
  items: string[]
  onItemsChange: (items: string[]) => void
  onBlur?: () => void
  placeholder?: string
  label?: string
  description?: string
  error?: string
  required?: boolean
  className?: string
  disabled?: boolean
  readOnly?: boolean
}

export function SingleFieldWithLabels({
  items,
  onItemsChange,
  onBlur,
  placeholder = "Entrez un élément...",
  label,
  description,
  error,
  required = false,
  className,
  disabled = false,
  readOnly = false
}: SingleFieldWithLabelsProps) {
  const isReadOnly = readOnly || disabled
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')

  const handleAddItem = () => {
    if (inputValue.trim() && !items.includes(inputValue.trim())) {
      onItemsChange([...items, inputValue.trim()])
      setInputValue('')
    }
  }

  // Filter out empty items to prevent empty chips
  const validItems = items.filter(item => item && item.trim() !== '')

  const handleRemoveItem = (index: number) => {
    const newItems = validItems.filter((_, i) => i !== index)
    onItemsChange(newItems)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddItem()
    }
  }

  return (
    <div className={cn("space-y-3", className)}>
      {label && (
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}

      {/* Input field with add button */}
      {!isReadOnly && (
        <div className="flex gap-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            onBlur={onBlur}
            placeholder={placeholder}
            className="flex-1"
          />
          <Button
            type="button"
            onClick={handleAddItem}
            disabled={!inputValue.trim() || items.includes(inputValue.trim())}
            size="sm"
            className="px-3"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Display items as labels */}
      {validItems.length > 0 && (
        <div className="flex flex-col min-[1150px]:flex-row min-[1150px]:flex-wrap gap-2">
          {validItems.map((item, index) => (
            <div
              key={index}
              className="flex items-start min-[1150px]:items-center gap-1.5 px-3 py-2 min-[1150px]:py-1 bg-primary/10 text-primary rounded-lg min-[1150px]:rounded-full text-sm border border-primary/20 w-full min-[1150px]:w-auto min-[1150px]:max-w-full"
            >
              <span className="break-words flex-1 min-w-0 text-left">{item}</span>
              {!isReadOnly && (
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="hover:bg-primary/20 rounded-full p-0.5 transition-colors flex-shrink-0"
                  title={t('common.delete', 'Delete')}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}
